/**
 * Enregistrement micro, tolérant aux navigateurs.
 *
 * Points de compatibilité traités ici :
 *  - iOS/Safari ne produit que du MP4/AAC ; Chrome/Firefox du WebM/Opus.
 *    On choisit le premier type supporté et on laisse le décodage au navigateur.
 *  - getUserMedia exige HTTPS (ou localhost) : on remonte une erreur claire.
 *  - Le niveau d'entrée est mesuré par AnalyserNode pour le vu-mètre.
 */
import { getContext, unlock } from './context';

const CANDIDATE_TYPES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4;codecs=mp4a.40.2',
  'audio/mp4',
  'audio/ogg;codecs=opus',
  'audio/ogg',
];

export function pickMimeType(): string | undefined {
  if (typeof MediaRecorder === 'undefined') return undefined;
  for (const t of CANDIDATE_TYPES) {
    if (MediaRecorder.isTypeSupported?.(t)) return t;
  }
  return undefined;
}

export function isRecordingSupported(): boolean {
  return (
    typeof MediaRecorder !== 'undefined' &&
    !!navigator.mediaDevices &&
    typeof navigator.mediaDevices.getUserMedia === 'function'
  );
}

export function micErrorMessage(err: unknown): string {
  const name = (err as { name?: string } | null)?.name ?? '';
  if (!window.isSecureContext) {
    return 'Le micro exige une page en HTTPS. Ouvre le site en https:// (ou en localhost pour tester).';
  }
  if (name === 'NotAllowedError' || name === 'SecurityError') {
    return "L'accès au micro a été refusé. Autorise-le dans les réglages du navigateur.";
  }
  if (name === 'NotFoundError' || name === 'OverconstrainedError') {
    return 'Aucun micro détecté sur cet appareil.';
  }
  if (name === 'NotReadableError') {
    return 'Le micro est déjà utilisé par une autre application.';
  }
  return "Impossible de démarrer l'enregistrement sur ce navigateur.";
}

export class Recorder {
  private rec: MediaRecorder | null = null;
  private stream: MediaStream | null = null;
  private chunks: Blob[] = [];
  private analyser: AnalyserNode | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private levelBuf: Float32Array | null = null;
  private startedAt = 0;

  /** Démarre. À appeler DANS un gestionnaire de geste utilisateur. */
  async start(): Promise<void> {
    if (!isRecordingSupported()) {
      throw new Error("Ce navigateur ne sait pas enregistrer. Importe un fichier à la place.");
    }
    await unlock();

    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
      },
    });

    const ctx = getContext();
    this.source = ctx.createMediaStreamSource(this.stream);
    this.analyser = ctx.createAnalyser();
    this.analyser.fftSize = 1024;
    this.levelBuf = new Float32Array(this.analyser.fftSize);
    this.source.connect(this.analyser);

    const mimeType = pickMimeType();
    this.rec = new MediaRecorder(this.stream, mimeType ? { mimeType } : undefined);
    this.chunks = [];
    this.rec.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) this.chunks.push(e.data);
    };
    this.rec.start(250);
    this.startedAt = performance.now();
  }

  /** Niveau RMS instantané, 0..1 — pour le vu-mètre. */
  level(): number {
    if (!this.analyser || !this.levelBuf) return 0;
    const a = this.analyser as AnalyserNode & {
      getFloatTimeDomainData?: (arr: Float32Array) => void;
    };
    if (typeof a.getFloatTimeDomainData !== 'function') return 0;
    a.getFloatTimeDomainData(this.levelBuf);
    let sum = 0;
    for (let i = 0; i < this.levelBuf.length; i++) sum += this.levelBuf[i] * this.levelBuf[i];
    return Math.min(1, Math.sqrt(sum / this.levelBuf.length) * 3.2);
  }

  elapsed(): number {
    return this.startedAt ? (performance.now() - this.startedAt) / 1000 : 0;
  }

  async stop(): Promise<Blob> {
    const rec = this.rec;
    if (!rec) throw new Error('Aucun enregistrement en cours.');

    const blob = await new Promise<Blob>((resolve) => {
      rec.onstop = () => resolve(new Blob(this.chunks, { type: rec.mimeType || 'audio/webm' }));
      if (rec.state !== 'inactive') rec.stop();
      else resolve(new Blob(this.chunks, { type: rec.mimeType || 'audio/webm' }));
    });

    this.dispose();
    return blob;
  }

  dispose(): void {
    this.rec = null;
    this.source?.disconnect();
    this.analyser?.disconnect();
    this.source = null;
    this.analyser = null;
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
    this.startedAt = 0;
  }
}

/**
 * Empêche l'écran de s'éteindre pendant l'enregistrement (Chrome, Edge, Safari 16.4+).
 * Sur iOS plus ancien, l'utilisateur doit garder l'écran allumé lui-même.
 */
type WakeLockNav = Navigator & {
  wakeLock?: { request(type: 'screen'): Promise<{ release(): Promise<void> }> };
};

export async function requestWakeLock(): Promise<{ release(): Promise<void> } | null> {
  const nav = navigator as WakeLockNav;
  if (!nav.wakeLock) return null;
  try {
    return await nav.wakeLock.request('screen');
  } catch {
    return null;
  }
}
